import { Account, AccountAttachmentRequirementStatus } from "models/Account";
import { Person } from "models/Person";
import React, { useCallback, useEffect, useState } from "react";
import { Alert, Spinner } from "reactstrap";
import { getProjectHours, getWorkerProjects } from "services/AccountApi";
import AccountAttachmentPanel from "./AccountAttachmentPanel";
import AccountDetailCard from "./CustomerDetails/AccountDetailCard";
import AccountPersonManagementCard from "./PersonManagement/AccountPersonManagementCard";
import SecurityPage from "./Security/SecurityPage";
import AccountPushNotifications from "./AccountPushNotifications";
import { useAccountTranslation } from "./translations";

interface Props {
    customer: Account;
    person: Person;
    setPerson: (person: Person) => void;
    onCustomerChange: (customer: Account) => void;
}

const SettingsSection: React.FC<{
    icon: string;
    title: string;
    description: string;
    children: React.ReactNode;
}> = ({ icon, title, description, children }) =>
    <section className="portal-settings-section">
        <div className="portal-settings-heading">
            <span><i className={`mdi ${icon}`} aria-hidden="true" /></span>
            <div>
                <h2>{title}</h2>
                <p>{description}</p>
            </div>
        </div>
        <div className="portal-settings-content">{children}</div>
    </section>;

const AccountSettingsPage: React.FC<Props> = ({ customer, person, setPerson, onCustomerChange }) => {
    const { t } = useAccountTranslation();
    const [requirements, setRequirements] = useState<AccountAttachmentRequirementStatus[]>([]);
    const [attachmentsCompliant, setAttachmentsCompliant] = useState(true);
    const [attachmentsLoading, setAttachmentsLoading] = useState(true);
    const [attachmentsError, setAttachmentsError] = useState(false);

    const loadAttachments = useCallback(async () => {
        setAttachmentsLoading(true);
        setAttachmentsError(false);
        try {
            const projects = await getWorkerProjects(customer.id);
            const hourViews = await Promise.all(projects.map(project => getProjectHours(project.id, customer.id)));
            const byRequirement = new Map<string, AccountAttachmentRequirementStatus>();
            hourViews.forEach(hours => (hours.attachment_requirements ?? []).forEach(requirement => {
                byRequirement.set(requirement.requirement.id, requirement);
            }));
            const values = Array.from(byRequirement.values());
            setRequirements(values);
            setAttachmentsCompliant(values
                .filter(value => value.requirement.required_for_hours)
                .every(value => value.status === "approved"));
        } catch {
            setAttachmentsError(true);
        } finally {
            setAttachmentsLoading(false);
        }
    }, [customer.id]);

    useEffect(() => {
        loadAttachments();
    }, [loadAttachments]);

    return <div className="portal-settings-page">
        <header className="portal-page-heading">
            <span className="portal-eyebrow">{t("myDetails")}</span>
            <h1>{t("myDetails")}</h1>
            <p>{t("settingsIntro")}</p>
        </header>

        <div className="portal-settings-stack">
            <AccountDetailCard customer={customer} onCustomerChange={onCustomerChange} />

            {attachmentsLoading
                ? <section className="portal-settings-section"><Spinner size="sm" /></section>
                : attachmentsError
                    ? <Alert color="danger">{t("dashboardLoadError")}</Alert>
                    : <AccountAttachmentPanel
                        accountId={customer.id}
                        compliant={attachmentsCompliant}
                        requirements={requirements}
                        onReload={loadAttachments}
                    />}

            <SettingsSection
                icon="mdi-account-outline"
                title={t("profileSettings")}
                description={t("profileSettingsDescription")}
            >
                <AccountPersonManagementCard setPerson={setPerson} person={person} customer={customer} />
            </SettingsSection>

            <SettingsSection
                icon="mdi-shield-lock-outline"
                title={t("security")}
                description={t("securitySettingsDescription")}
            >
                <SecurityPage />
            </SettingsSection>

            <SettingsSection
                icon="mdi-bell-outline"
                title={t("pushNotifications")}
                description={t("pushNotificationsDescription")}
            >
                <AccountPushNotifications accountId={customer.id} />
            </SettingsSection>
        </div>
    </div>;
};

export default AccountSettingsPage;
